import { css, useTheme } from '@emotion/react';
import { formatDuration, TONE_COLORS } from '@sonematic/shared';
import type { Tone } from '@sonematic/shared';
import { Download, Music, Pause, Play } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { GenerationPartitionStatus } from '@/lib/api-client';
import { fontSizes, spacing } from '@/theme/tokens';

export interface MusicPlayerSection {
  position: number;
  detectedTone: string | null;
  generationStatus: 'ready' | 'pending' | 'analyzing_tone' | 'generating' | 'error';
  audioUrl: string | null;
  audioDurationMs: number | null;
  audioOffsetMs: number;
  partitionIndex: number;
}

export interface MusicPlayerProps {
  sections: MusicPlayerSection[];
  partitions: GenerationPartitionStatus[];
  totalDurationMs: number;
}

const SECTION_COLORS = [
  '#6a5fc1',
  '#79628c',
  '#422082',
  '#c2ef4e',
  '#ffb287',
  '#fa7faa',
  '#7b71d1',
  '#584674',
];

function getSectionColor(index: number, tone?: string | null): string {
  if (tone && TONE_COLORS[tone as Tone]) return TONE_COLORS[tone as Tone].bg;
  return SECTION_COLORS[index % SECTION_COLORS.length];
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getSectionLabel(section: MusicPlayerSection): string {
  return section.detectedTone
    ? capitalize(section.detectedTone)
    : `Section ${section.position + 1}`;
}

export function MusicPlayer({ sections, partitions, totalDurationMs }: MusicPlayerProps) {
  const theme = useTheme();

  const readyPartitionIndices = new Set(
    partitions.filter((p) => p.status === 'ready' && p.audioUrl).map((p) => p.partitionIndex),
  );
  const readySections = sections.filter(
    (s) =>
      s.generationStatus === 'ready' && (s.audioUrl || readyPartitionIndices.has(s.partitionIndex)),
  );

  // Fall back to summing section durations if totalDurationMs is 0/null
  const effectiveTotalMs =
    totalDurationMs > 0
      ? totalDurationMs
      : readySections.reduce((sum, s) => sum + (s.audioDurationMs ?? 0), 0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const progressPercent = effectiveTotalMs > 0 ? (elapsedMs / effectiveTotalMs) * 100 : 0;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentPartitionRef = useRef<number | null>(null);

  // Compute cumulative durations for each ready section (sum of preceding section durations)
  const cumulativeMs = useRef<number[]>([]);
  cumulativeMs.current = readySections.reduce<number[]>((acc, section, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + (readySections[i - 1].audioDurationMs ?? 0));
    return acc;
  }, []);

  // Stable refs for event listener callbacks (avoids stale closures)
  const timeUpdateRef = useRef<() => void>(() => {});
  const endedRef = useRef<() => void>(() => {});

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      currentPartitionRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, [cleanupAudio]);

  const resetToIdle = useCallback(() => {
    cleanupAudio();
    setIsPlaying(false);
    setCurrentSectionIndex(null);
    setElapsedMs(0);
  }, [cleanupAudio]);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || currentSectionIndex === null) return;
    if (isDraggingRef.current) return;

    const section = readySections[currentSectionIndex];
    if (!section) return;

    const sectionOffsetSec = section.audioOffsetMs / 1000;
    const sectionDurationSec = (section.audioDurationMs ?? 0) / 1000;
    const sectionEndSec = sectionOffsetSec + sectionDurationSec;

    // Update elapsed time
    const playedInSectionMs = (audio.currentTime - sectionOffsetSec) * 1000;
    const newElapsed = cumulativeMs.current[currentSectionIndex] + Math.max(0, playedInSectionMs);
    setElapsedMs(newElapsed);

    // Check if we've passed the section boundary
    if (audio.currentTime >= sectionEndSec) {
      const nextIndex = currentSectionIndex + 1;
      if (nextIndex < readySections.length) {
        const nextSection = readySections[nextIndex];
        const nextPartition = partitions.find(
          (p) => p.partitionIndex === nextSection.partitionIndex,
        );
        const samePartition =
          nextPartition?.audioUrl != null &&
          nextSection.partitionIndex === section.partitionIndex &&
          // Only reuse audio in true partition mode (not standalone sections)
          partitions.some((p) => p.partitionIndex === section.partitionIndex && p.audioUrl);

        if (samePartition) {
          // Same partition — just advance the index
          setCurrentSectionIndex(nextIndex);
        } else {
          // Different source — need new audio element
          audio.pause();

          const nextUrl = nextPartition?.audioUrl ?? nextSection.audioUrl;
          if (nextUrl) {
            const newAudio = new Audio(nextUrl);
            newAudio.currentTime = nextSection.audioOffsetMs / 1000;
            audioRef.current = newAudio;
            currentPartitionRef.current = nextPartition?.audioUrl
              ? nextSection.partitionIndex
              : -(nextIndex + 1);
            setCurrentSectionIndex(nextIndex);

            const onTimeUpdate = () => timeUpdateRef.current();
            const onEnded = () => endedRef.current();
            newAudio.addEventListener('timeupdate', onTimeUpdate);
            newAudio.addEventListener('ended', onEnded);
            void newAudio.play();
          }
        }
      } else {
        // Reached end of all sections — reset to idle
        resetToIdle();
      }
    }
  }, [currentSectionIndex, readySections, partitions, resetToIdle]);

  const handleEnded = useCallback(() => {
    resetToIdle();
  }, [resetToIdle]);

  // Keep refs in sync with latest callbacks
  useEffect(() => {
    timeUpdateRef.current = handleTimeUpdate;
    endedRef.current = handleEnded;
  }, [handleTimeUpdate, handleEnded]);

  const playFromSection = useCallback(
    (index: number, offsetWithinSectionMs = 0) => {
      const section = readySections[index];
      if (!section) return;

      // Resolve audio source: partition mode (shared audio file) vs standalone (per-section URL)
      const partition = partitions.find((p) => p.partitionIndex === section.partitionIndex);
      const resolvedUrl = partition?.audioUrl ?? section.audioUrl;
      if (!resolvedUrl) return;

      const isPartitionMode = partition?.audioUrl != null;
      // Use a unique key per audio source for reuse logic (-1 for standalone sections)
      const sourceKey = isPartitionMode ? section.partitionIndex : -(index + 1);
      const seekTimeSec = section.audioOffsetMs / 1000 + offsetWithinSectionMs / 1000;

      // Check if we can reuse existing audio (same source)
      if (audioRef.current && currentPartitionRef.current === sourceKey) {
        audioRef.current.currentTime = seekTimeSec;
        setCurrentSectionIndex(index);
        setIsPlaying(true);

        setElapsedMs(cumulativeMs.current[index] + offsetWithinSectionMs);

        void audioRef.current.play();
        return;
      }

      // Need new audio element
      cleanupAudio();

      const audio = new Audio(resolvedUrl);
      audio.currentTime = seekTimeSec;
      audioRef.current = audio;
      currentPartitionRef.current = sourceKey;

      // Use stable refs for event listeners
      const onTimeUpdate = () => timeUpdateRef.current();
      const onEnded = () => endedRef.current();
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('ended', onEnded);

      setCurrentSectionIndex(index);
      setIsPlaying(true);
      setElapsedMs(cumulativeMs.current[index] + offsetWithinSectionMs);

      void audio.play();
    },
    [readySections, partitions, cleanupAudio],
  );

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else if (audioRef.current && currentSectionIndex !== null) {
      // Resume
      void audioRef.current.play();
      setIsPlaying(true);
    } else {
      // Start from beginning
      playFromSection(0);
    }
  }, [isPlaying, currentSectionIndex, playFromSection]);

  const handleDownload = useCallback(() => {
    const partition = partitions.find((p) => p.status === 'ready' && p.audioUrl);
    if (!partition?.audioUrl) return;

    const a = document.createElement('a');
    a.href = partition.audioUrl;
    a.download = 'music.m4a';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [partitions]);

  const hasDownloadableAudio = partitions.some((p) => p.status === 'ready' && p.audioUrl);

  const isDraggingRef = useRef(false);
  const scrubberRef = useRef<HTMLDivElement | null>(null);

  const seekToFraction = useCallback(
    (fraction: number) => {
      const targetMs = Math.max(0, Math.min(1, fraction)) * effectiveTotalMs;
      setElapsedMs(targetMs);
      // Find which section this falls in
      let targetIndex = 0;
      for (let i = 0; i < readySections.length; i++) {
        const sectionEndMs = cumulativeMs.current[i] + (readySections[i].audioDurationMs ?? 0);
        if (targetMs < sectionEndMs) {
          targetIndex = i;
          break;
        }
        if (i === readySections.length - 1) {
          targetIndex = i;
        }
      }
      const offsetWithinSectionMs = targetMs - cumulativeMs.current[targetIndex];
      playFromSection(targetIndex, offsetWithinSectionMs);
    },
    [readySections, playFromSection, effectiveTotalMs],
  );

  const getFractionFromEvent = useCallback((clientX: number) => {
    const el = scrubberRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const handleScrubberMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      isDraggingRef.current = true;
      const fraction = getFractionFromEvent(e.clientX);
      setElapsedMs(fraction * effectiveTotalMs);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const moveFraction = getFractionFromEvent(moveEvent.clientX);
        setElapsedMs(moveFraction * effectiveTotalMs);
      };

      const handleMouseUp = (upEvent: MouseEvent) => {
        isDraggingRef.current = false;
        const upFraction = getFractionFromEvent(upEvent.clientX);
        seekToFraction(upFraction);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [getFractionFromEvent, seekToFraction, effectiveTotalMs],
  );

  const isActive = currentSectionIndex !== null;
  const currentSection = isActive ? readySections[currentSectionIndex] : null;

  // Header text
  const headerTitle = isActive ? getSectionLabel(currentSection!) : 'Generated Music';
  const headerSubtitle = isActive
    ? `Section ${currentSectionIndex + 1} of ${readySections.length}`
    : `${readySections.length} sections \u00b7 ${formatDuration(effectiveTotalMs)}`;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.sm};
      `}
    >
      {/* Mini player card */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.sm};
          background: ${theme.colors.surfaceElevated};
          border: 1px solid ${theme.colors.border};
          border-radius: 12px;
          padding: ${spacing.md};
        `}
      >
        {/* Header row */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.sm};
          `}
        >
          {/* Gradient music icon */}
          <div
            css={css`
              width: 40px;
              height: 40px;
              border-radius: 8px;
              background: linear-gradient(
                135deg,
                ${theme.colors.primary},
                ${theme.colors.primaryHover}
              );
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            `}
          >
            <Music size={20} color={theme.colors.primaryText} />
          </div>

          {/* Title + subtitle */}
          <div
            css={css`
              flex: 1;
              min-width: 0;
            `}
          >
            <div
              css={css`
                font-size: ${fontSizes.sm};
                font-weight: 600;
                color: ${theme.colors.text};
              `}
            >
              {headerTitle}
            </div>
            <div
              css={css`
                font-size: ${fontSizes.xs};
                color: ${theme.colors.textSecondary};
              `}
            >
              {headerSubtitle}
            </div>
          </div>

          {/* Play/Pause button */}
          <button
            type="button"
            aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={handlePlayPause}
            css={css`
              width: 34px;
              height: 34px;
              border-radius: 50%;
              border: none;
              background: ${theme.colors.primary};
              color: ${theme.colors.primaryText};
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              flex-shrink: 0;
              &:hover {
                background: ${theme.colors.primaryHover};
              }
            `}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
        </div>

        {/* Download button */}
        {hasDownloadableAudio && (
          <button
            type="button"
            aria-label="Download music"
            onClick={handleDownload}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.xs};
              align-self: flex-end;
              padding: 4px 8px;
              border-radius: 6px;
              border: none;
              background: transparent;
              color: ${theme.colors.textSecondary};
              font-size: ${fontSizes.xs};
              cursor: pointer;
              &:hover {
                color: ${theme.colors.text};
                background: ${theme.colors.surface};
              }
            `}
          >
            <Download size={14} />
            Download
          </button>
        )}

        {/* Progress bar */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <div
            ref={scrubberRef}
            role="slider"
            aria-label="Playback progress"
            aria-valuenow={Math.round(progressPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            onMouseDown={handleScrubberMouseDown}
            css={css`
              width: 100%;
              height: 12px;
              display: flex;
              align-items: center;
              cursor: pointer;
              position: relative;
            `}
          >
            {/* Track background */}
            <div
              css={css`
                position: absolute;
                left: 0;
                right: 0;
                height: 4px;
                background: ${theme.colors.border};
                border-radius: 2px;
              `}
            />
            {/* Progress fill */}
            <div
              css={css`
                position: absolute;
                left: 0;
                height: 4px;
                width: ${progressPercent}%;
                background: ${theme.colors.primary};
                border-radius: 2px;
                transition: ${isDraggingRef.current ? 'none' : 'width 0.1s linear'};
              `}
            />
            {/* Section tick marks */}
            {readySections.slice(1).map((section, i) => {
              const tickMs = cumulativeMs.current[i + 1];
              const tickPercent = effectiveTotalMs > 0 ? (tickMs / effectiveTotalMs) * 100 : 0;
              return (
                <div
                  key={section.position}
                  css={css`
                    position: absolute;
                    left: ${tickPercent}%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    width: 2px;
                    height: 8px;
                    background: ${theme.colors.textSecondary};
                    border-radius: 1px;
                    opacity: 0.4;
                    pointer-events: none;
                  `}
                />
              );
            })}
          </div>
          {/* Time labels */}
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              font-size: ${fontSizes.xs};
              color: ${theme.colors.textSecondary};
              font-variant-numeric: tabular-nums;
            `}
          >
            <span>{formatDuration(elapsedMs)}</span>
            <span>{formatDuration(effectiveTotalMs)}</span>
          </div>
        </div>
      </div>

      {/* Chapter list */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.xs};
        `}
      >
        <div
          css={css`
            font-size: ${fontSizes.xs};
            font-weight: 600;
            color: ${theme.colors.textSecondary};
            text-transform: uppercase;
            letter-spacing: 0.05em;
            padding: 0 ${spacing.xs};
          `}
        >
          Sections
        </div>
        {readySections.map((section, index) => {
          const color = getSectionColor(index, section.detectedTone);
          const label = getSectionLabel(section);
          const isActiveRow = currentSectionIndex === index;
          const isPlayed = currentSectionIndex !== null && index < currentSectionIndex;
          const isUpcoming = currentSectionIndex !== null && index > currentSectionIndex;

          return (
            <button
              key={section.position}
              type="button"
              aria-label={`Jump to ${label}`}
              onClick={() => playFromSection(index)}
              {...(isActiveRow ? { 'data-active': 'true' } : {})}
              {...(isPlayed ? { 'data-played': 'true' } : {})}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.sm};
                padding: ${spacing.xs} ${spacing.sm};
                border-radius: 6px;
                cursor: pointer;
                border: none;
                background: ${isActiveRow ? `${color}15` : 'transparent'};
                text-align: left;
                width: 100%;
                opacity: ${isPlayed ? 0.6 : isUpcoming ? 0.7 : 1};
                &:hover {
                  background: ${isActiveRow ? `${color}20` : theme.colors.surfaceElevated};
                }
              `}
            >
              {/* Tone color dot / active bar */}
              <div
                css={css`
                  width: 4px;
                  height: ${isActiveRow ? '14px' : '4px'};
                  border-radius: ${isActiveRow ? '2px' : '50%'};
                  background: ${color};
                  flex-shrink: 0;
                  transition:
                    height 0.2s ease,
                    border-radius 0.2s ease;
                `}
              />
              {/* Section label */}
              <span
                css={css`
                  flex: 1;
                  font-size: ${fontSizes.sm};
                  font-weight: ${isActiveRow ? 600 : 400};
                  color: ${isActiveRow
                    ? color
                    : isPlayed
                      ? theme.colors.textSecondary
                      : theme.colors.text};
                `}
              >
                {label}
              </span>
              {/* Duration */}
              <span
                css={css`
                  font-size: ${fontSizes.xs};
                  color: ${isActiveRow ? color : theme.colors.textSecondary};
                  opacity: ${isActiveRow ? 0.8 : 1};
                `}
              >
                {formatDuration(section.audioDurationMs ?? 0)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
